import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Palmtree } from 'lucide-react';
import { SITE } from '@/config';
import { trackEvent } from '@/lib/tracking';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Overview', to: '/overview' },
  {
    label: 'Homes',
    children: [
      { label: '1 BHK', to: '/1-bhk' },
      { label: '2 BHK', to: '/2-bhk' },
      { label: '2.5 BHK', to: '/2-5-bhk' },
    ],
  },
  { label: 'Floor Plans', to: '/provident-palm-vista-floor-plan' },
  { label: 'Amenities', to: '/provident-palm-vista-amenities' },
  { label: 'Location', to: '/provident-palm-vista-location' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'RERA', to: '/provident-palm-vista-rera' },
  { label: 'FAQs', to: '/faqs' },
  { label: 'Contact', to: '/contact' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || location.pathname !== '/';

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-[80] transition-all duration-300 ${
        solid ? 'bg-ivory/90 backdrop-blur-xl shadow-sm border-b border-line' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <span className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${solid ? 'bg-palm text-gold' : 'bg-ivory/15 text-gold backdrop-blur'}`}>
              <Palmtree className="h-4.5 w-4.5 h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>
            <span className={`font-display text-lg sm:text-xl font-semibold tracking-tight leading-none transition-colors ${solid ? 'text-palm' : 'text-ivory'}`}>
              Provident <span className="text-gold-grad">Palm Vista</span>
              <span className={`block text-[9px] font-sans font-semibold tracking-[0.3em] uppercase mt-0.5 ${solid ? 'text-muted-foreground' : 'text-ivory/70'}`}>Shilphata</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
            {NAV.slice(1).map((item) =>
              item.children ? (
                <div key={item.label} className="relative group">
                  <button data-testid="nav-homes-dropdown" className={`flex items-center gap-1 text-[13px] font-semibold tracking-wide transition-colors ${solid ? 'text-palm hover:text-gold' : 'text-ivory/90 hover:text-gold'}`}>
                    {item.label} <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-white rounded-2xl shadow-xl border border-line p-2 min-w-[140px]">
                      {item.children.map((c) => (
                        <NavLink key={c.to} to={c.to} data-testid={`nav-${c.label.replace(/\s|\.|-/g, '').toLowerCase()}`}
                          className="block px-4 py-2.5 text-sm font-medium text-palm rounded-xl hover:bg-paper hover:text-gold transition-colors">
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                  className={`text-[13px] font-semibold tracking-wide transition-colors ${solid ? 'text-palm hover:text-gold' : 'text-ivory/90 hover:text-gold'}`}>
                  {item.label}
                </NavLink>
              )
            )}
            <a href={SITE.tel} data-testid="nav-call-btn" onClick={() => trackEvent('phone_click', { from: 'navbar' })}
              className="flex items-center gap-2 rounded-full bg-gold text-palm px-5 py-2.5 text-[13px] font-bold shadow hover:brightness-110 transition-all">
              <Phone className="h-3.5 w-3.5" /> {SITE.phoneDisplay}
            </a>
          </nav>

          <button className={`lg:hidden p-2 ${solid ? 'text-palm' : 'text-ivory'}`} onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-ivory border-t border-line shadow-xl max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="px-6 py-4 flex flex-col" aria-label="Mobile navigation">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="border-b border-line/70">
                  <p className="py-3 text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">{item.label}</p>
                  <div className="flex gap-2 pb-3">
                    {item.children.map((c) => (
                      <NavLink key={c.to} to={c.to} data-testid={`mobile-nav-${c.label.replace(/\s|\.|-/g, '').toLowerCase()}`}
                        className="rounded-full bg-paper px-4 py-2 text-sm font-semibold text-palm">
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} data-testid={`mobile-nav-${item.label.replace(/\s/g, '-').toLowerCase()}`}
                  className="py-3 text-sm font-semibold text-palm border-b border-line/70 last:border-0">
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
